import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { APISD } from "../../../../../../../config/apisd";
import ToastError from "../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../NotificationToast/ToastSuccess";
import { studentPhoto } from "./modal/preview-foto";

const studentKey = (student, index) => String(student?.ysb_student_id ?? student?.id ?? `row-${index}`);

const MAX_BYTES = 500000; // 0,5 MB desimal
const formatSize = (bytes) => `${(bytes / 1000000).toFixed(2)} MB`;

async function compressPhoto(file) {
  const sourceUrl = URL.createObjectURL(file);
  let image;

  try {
    image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Foto tidak dapat dibaca."));
      element.src = sourceUrl;
    });

    let scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));

    for (let resize = 0; resize < 8; resize += 1) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

      const context = canvas.getContext("2d");

      if (!context) throw new Error("Browser tidak dapat memproses foto.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      for (const quality of [0.82, 0.7, 0.58, 0.45, 0.34]) {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
        if (!blob) throw new Error("Kompresi foto gagal.");
        if (blob.size <= MAX_BYTES) {
          const name = `${file.name.replace(/\.[^.]+$/, "")}.jpg`;
          return new File([blob], name, { type: "image/jpeg" });
        }
      }
      scale *= 0.75;
    }

    throw new Error("Foto tidak dapat dikompres sampai 0,5 MB. Coba foto lain.");
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

// Pesan sukses/gagal diambil dari respons API, bukan teks toast statis.
const backendMessage = (data, fallback) =>
  typeof data?.message === "string" && data.message.trim() ? data.message : fallback;

const responseFailed = (data) =>
  data?.error === true || data?.success === false || data?.status === false ||
  (typeof data?.status === "number" && data.status >= 400);

const requestErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const details = data?.errors
    ? Object.values(data.errors).flat().filter(Boolean).join(" ")
    : "";
  return backendMessage(data, details || error?.message || fallback);
};

// Hanya untuk URL foto dari server agar browser tidak memakai cache foto lama.
const refreshedPhotoUrl = (url, version) => {
  if (!version || typeof url !== "string" || /^(blob:|data:)/i.test(url)) return url;
  const [path, hash] = url.split("#", 2);
  return `${path}${path.includes("?") ? "&" : "?"}photo_updated=${version}${hash ? `#${hash}` : ""}`;
};

// onRefresh: fungsi GET daftar siswa dari halaman induk (parent).
export default function FotoProfilTab({ students = [], contextKey, uploadContext = {}, onRefresh }) {
  const [activePhoto, setActivePhoto] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hiddenPhotos, setHiddenPhotos] = useState({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const photoTopRef = useRef(null);
  const activeContext = useRef(contextKey);
  activeContext.current = contextKey;
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState({});
  const [photoVersions, setPhotoVersions] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState("");
  const [processing, setProcessing] = useState({});
  const jobIds = useRef({});
  const inputRefs = useRef({});
  const draftsRef = useRef(drafts);
  const savedRef = useRef(saved);
  draftsRef.current = drafts;
  savedRef.current = saved;

  useEffect(() => {
    setDrafts((previous) => {
      Object.values(previous).forEach((item) => URL.revokeObjectURL(item.url));
      return {};
    });

    setSaved((previous) => {
      Object.values(previous).forEach((item) => URL.revokeObjectURL(item.url));
      return {};
    });
    
    setActivePhoto(null);
    setDeleteTarget(null);
    setHiddenPhotos({});
    setPhotoVersions({});
    setSearch("");
    setPage(1);
    setNotice("");
    setProcessing({});
    jobIds.current = {};
  }, [contextKey]);

  useEffect(() => () => {
    Object.values(draftsRef.current).forEach((item) => URL.revokeObjectURL(item.url));
    Object.values(savedRef.current).forEach((item) => URL.revokeObjectURL(item.url));
  }, []);

  // Sesudah menyimpan, gulir kembali ke awal daftar foto (tanpa reload halaman).
  // scrollIntoView juga bekerja bila halaman memakai kontainer dengan overflow.
  const scrollToPhotoTop = () => {
    window.requestAnimationFrame(() => {
      photoTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Refresh dilakukan oleh Home melalui GET siswa/kelas aktif.
  // Pratinjau lokal dipertahankan bila GET gagal atau server belum mengirim URL foto.
  const refreshAfterChange = async (keys, requestContext, action = "save") => {
    if (!keys.length || typeof onRefresh !== "function" || activeContext.current !== requestContext) return;
    try {
      const refreshedStudents = await onRefresh();
      if (activeContext.current !== requestContext || !Array.isArray(refreshedStudents)) return;

      const verifiedKeys = keys.filter((key) => {
        const student = refreshedStudents.find((row, index) => studentKey(row, index) === key);
        if (!student) return false;
        const hasPhoto = Boolean(studentPhoto(student));
        return action === "delete" ? !hasPhoto : hasPhoto;
      });
      if (!verifiedKeys.length) return;

      if (action === "save") {
        setSaved((previous) => {
          const next = { ...previous };
          verifiedKeys.forEach((key) => {
            if (next[key]) URL.revokeObjectURL(next[key].url);
            delete next[key];
          });
          return next;
        });
      } else {
        setHiddenPhotos((previous) => {
          const next = { ...previous };
          verifiedKeys.forEach((key) => { delete next[key]; });
          return next;
        });
      }

      // Paksa browser mengambil foto terkini bila nama URL foto server tetap sama.
      setPhotoVersions((previous) => {
        const next = { ...previous };
        const version = Date.now();
        verifiedKeys.forEach((key) => { next[key] = version; });
        return next;
      });
    } catch (error) {
      // POST/DELETE tetap dianggap berhasil; cukup pertahankan hasil preview lokal.
      console.error("Gagal memuat ulang foto profil siswa:", error);
    }
  };

  const rows = useMemo(() => (Array.isArray(students) ? students : [])
    .map((student, index) => ({ student, key: studentKey(student, index) }))
    .filter(({ student }) => `${student?.nama_siswa || student?.name_student || ""} ${student?.nisn || ""}`
      .toLocaleLowerCase("id").includes(search.trim().toLocaleLowerCase("id")))
    .sort((a, b) => {
      const orderA = Number(a.student?.order_absen);
      const orderB = Number(b.student?.order_absen);
      const validA = a.student?.order_absen !== null && a.student?.order_absen !== "" && Number.isFinite(orderA);
      const validB = b.student?.order_absen !== null && b.student?.order_absen !== "" && Number.isFinite(orderB);
      if (validA && validB && orderA !== orderB) return orderA - orderB;
      if (validA !== validB) return validA ? 1 : -1;
      return String(a.student?.nama_siswa || a.student?.name_student || "")
        .localeCompare(String(b.student?.nama_siswa || b.student?.name_student || ""), "id");
    }), [students, search]);

  const pageCount = Math.max(1, Math.ceil(rows.length / 12));

  const visible = rows.slice((Math.min(page, pageCount) - 1) * 12, Math.min(page, pageCount) * 12);

  const count = Object.keys(drafts).length;

  const stageFile = async (key, file) => {
    if (!file || savingRef.current) return;
    if (!file.type.startsWith("image/")) {
      setNotice("Pilih file gambar untuk foto siswa.");
      return;
    }

    const stageContext = activeContext.current;
    const job = (jobIds.current[key] || 0) + 1;

    jobIds.current[key] = job;

    setProcessing((previous) => ({ ...previous, [key]: true }));
    setNotice("");
    try {
      const compressed = await compressPhoto(file);
      if (activeContext.current !== stageContext || jobIds.current[key] !== job) return;
      const url = URL.createObjectURL(compressed);
      setDrafts((previous) => {
        if (previous[key]) URL.revokeObjectURL(previous[key].url);
        return { ...previous, [key]: { file: compressed, url } };
      });
      setSaved((previous) => {
        if (previous[key]) URL.revokeObjectURL(previous[key].url);
        const next = { ...previous };
        delete next[key];
        return next;
      });
    } catch (error) {
      if (activeContext.current === stageContext && jobIds.current[key] === job) setNotice(error.message || "Kompresi foto gagal.");
    } finally {
      if (activeContext.current === stageContext && jobIds.current[key] === job) {
        setProcessing((previous) => ({ ...previous, [key]: false }));
      }
    }
  };

  const removePhoto = (key, allowWhileSaving = false) => {
    if (savingRef.current && !allowWhileSaving) return;
    jobIds.current[key] = (jobIds.current[key] || 0) + 1;
    setProcessing((previous) => ({ ...previous, [key]: false }));
    setDrafts((previous) => {
      if (previous[key]) URL.revokeObjectURL(previous[key].url);
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setSaved((previous) => {
      if (previous[key]) URL.revokeObjectURL(previous[key].url);
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setNotice("Foto pilihan dihapus dari pratinjau.");
  };

  const confirmRemovePhoto = async () => {
    if (!deleteTarget || savingRef.current) return;

    const { key, persisted } = deleteTarget;

    // Jika yang dihapus masih berupa foto draft / belum tersimpan ke server,
    // cukup hapus dari pratinjau lokal.
    if (!persisted) {
      removePhoto(key);
      setActivePhoto(null);
      setDeleteTarget(null);
      return;
    }

    const student = students.find((row, index) => studentKey(row, index) === key);
    const studentId = student?.ysb_student_id ?? student?.id;

    if (!studentId) {
      ToastError.fire({ icon: "error", title: "ID siswa tidak ditemukan." });
      return;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      const requiredFields = [
        "ysb_academic_year_id",
        "ysb_semester_id",
        "ysb_branch_id",
        "ysb_school_id",
        "ysb_grade_level_id",
        "ysb_kelas_id",
      ];

      const payload = {};

      requiredFields.forEach((field) => {
        const value = uploadContext[field];
        if (value === undefined || value === null || value === "") {
          throw new Error("Data filter belum lengkap. Pilih filter dan tampilkan data terlebih dahulu.");
        }
        payload[field] = value;
      });

      const userId = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");
      if (!userId) throw new Error("ID pengguna login tidak ditemukan. Silakan login kembali.");

      payload.ysb_student_id = [studentId];
      payload.user_id = userId;

      const response = await APISD.delete("/api/student-kelas/delete-photo", {
        headers: { Authorization: localStorage.getItem("token") },
        data: payload,
      });

      if (responseFailed(response.data)) {
        throw new Error(backendMessage(response.data, "Foto gagal dihapus."));
      }

      const message = backendMessage(response.data, "Foto berhasil dihapus.");
      removePhoto(key, true);
      setHiddenPhotos((previous) => ({ ...previous, [key]: true }));
      setNotice(message);
      setActivePhoto(null);
      setDeleteTarget(null);
      ToastSuccess.fire({ icon: "success", title: message });

      // Setelah hapus, samakan lagi foto di tab dengan hasil GET terbaru.
      await refreshAfterChange([key], contextKey, "delete");
    } catch (error) {
      const message = requestErrorMessage(error, "Foto gagal dihapus. Silakan coba kembali.");
      setNotice(message);
      ToastError.fire({ icon: "error", title: message });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (savingRef.current || !count || Object.values(processing).some(Boolean)) return;
    const snapshot = { ...drafts };
    const requestContext = contextKey;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = new FormData();
      const fields = ["ysb_academic_year_id", "ysb_semester_id", "ysb_branch_id", "ysb_school_id", "ysb_grade_level_id", "ysb_kelas_id"];
      fields.forEach((field) => {
        const value = uploadContext[field];
        if (value === undefined || value === null || value === "") throw new Error("Data filter belum lengkap. Pilih filter dan tampilkan data terlebih dahulu.");
        payload.append(field, String(value));
      });
      const userId = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");
      if (!userId) throw new Error("ID pengguna login tidak ditemukan. Silakan login kembali.");
      payload.append("user_id", userId);

      const studentIds = {};
      Object.entries(snapshot).forEach(([key, item]) => {
        const student = students.find((row, index) => studentKey(row, index) === key);
        const id = student?.ysb_student_id ?? student?.id;
        if (!id) throw new Error("ID siswa tidak ditemukan untuk foto yang dipilih.");
        studentIds[key] = String(id);
        payload.append(`foto[${id}]`, item.file, item.file.name);
      });

      const response = await APISD.post("/api/student-kelas/store-photo", payload, {
        headers: { Authorization: localStorage.getItem("token"), "Content-Type": "multipart/form-data" },
      });
      if (responseFailed(response.data)) {
        throw new Error(backendMessage(response.data, "Foto gagal disimpan."));
      }

      // Backend dapat mengembalikan hasil sebagian, misalnya "1 dari 2 foto berhasil".
      // Hanya foto yang tercantum di data.success yang dianggap tersimpan.
      const successList = response.data?.data?.success;
      const successIds = Array.isArray(successList)
        ? new Set(successList.map((item) => String(
            (item && typeof item === "object")
              ? (item.ysb_student_id ?? item.student_id ?? item.id ?? "")
              : item
          )))
        : null;
      const savedKeys = Object.keys(snapshot).filter((key) =>
        !successIds || successIds.has(studentIds[key])
      );
      const message = backendMessage(response.data, "Foto berhasil disimpan.");

      if (activeContext.current === requestContext) {
        if (savedKeys.length) {
          setSaved((previous) => {
            const next = { ...previous };
            savedKeys.forEach((key) => {
              if (next[key]) URL.revokeObjectURL(next[key].url);
              next[key] = snapshot[key];
            });
            return next;
          });
          setDrafts((previous) => {
            const next = { ...previous };
            savedKeys.forEach((key) => { delete next[key]; });
            return next;
          });
          setHiddenPhotos((previous) => {
            const next = { ...previous };
            savedKeys.forEach((key) => { delete next[key]; });
            return next;
          });
        }
        setNotice(message);
      }

      if (savedKeys.length) {
        ToastSuccess.fire({ icon: "success", title: message });
        // GET ulang tanpa reload, agar tab foto memuat data terbaru dari backend.
        await refreshAfterChange(savedKeys, requestContext);
        if (activeContext.current === requestContext) {
          setActivePhoto(null);
          scrollToPhotoTop();
        }
      } else {
        ToastError.fire({ icon: "error", title: message });
      }
    } catch (error) {
      const message = requestErrorMessage(error, "Foto gagal disimpan. Silakan coba kembali.");
      if (activeContext.current === requestContext) setNotice(message);
      ToastError.fire({ icon: "error", title: message });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const safeStr = (value) =>
    value === null || value === undefined ? "" : String(value);

  const toTitleCase = (value) => {
    const text = safeStr(value).toLowerCase().trim();

    if (!text) {
      return "";
    }

    return text.replace(/\w\S*/g, (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  };
  return (
    <>
      <div className="sims-foto-page" ref={photoTopRef} style={{ scrollMarginTop: "90px" }}>
        <div className="sims-foto-intro">
          <div>
            <h6>Foto profil siswa</h6>
            <p>Tarik foto ke kotak siswa, atau klik kotak kosong untuk memilih file. Klik foto untuk menampilkan tombol hapus.</p>
          </div>
          <span className="sims-foto-count">
            {count} foto siap disimpan
          </span>
        </div>

        <input 
          className="sims-foto-search" 
          type="search" 
          placeholder="Cari nama siswa atau NISN..." 
          aria-label="Cari siswa untuk foto profil" 
          value={search} 
          onChange={(event) => { 
            setSearch(event.target.value); 
            setPage(1); 
            }} 
        />

        {visible.length ? 
          <div className="sims-foto-grid">
            {visible.map(({ student, key }) => {
              const staged = drafts[key];

              const serverPhoto = !hiddenPhotos[key] ? studentPhoto(student) : null;
              const preview = staged?.url || saved[key]?.url || refreshedPhotoUrl(serverPhoto, photoVersions[key]);

              const name = student?.nama_siswa || student?.name_student || "-";

              const existing = !hiddenPhotos[key] && Boolean(studentPhoto(student));

              const persisted = !staged && Boolean(saved[key] || existing);

              return <article className="sims-foto-card" key={key}>
                <input 
                  ref={(node) => { inputRefs.current[key] = node; }}
                  type="file" 
                  accept="image/*" 
                  hidden 
                  aria-label={`Pilih foto ${name}`} 
                  onChange={(event) => { 
                    stageFile(key, event.target.files?.[0]); 
                    event.target.value = ""; 
                  }} />
                <div 
                  className={`sims-foto-photo ${activePhoto === key ? "is-selected" : ""}`}
                >
                  <button 
                    type="button" 
                    className="sims-foto-drop" 
                    disabled={saving} 

                    aria-label={preview ? `Pilih foto ${name} untuk menampilkan tombol hapus` : `Pilih atau tarik foto untuk ${name}`}

                    aria-pressed={Boolean(preview && activePhoto === key)} 

                    onClick={() => { 
                      if (preview) setActivePhoto((previous) => previous === key ? null : key); else inputRefs.current[key]?.click(); 
                    }} 
                    
                    onDragOver={(event) => { 
                      event.preventDefault(); event.currentTarget.classList.add("is-over"); 
                    }} 
                    
                    onDragLeave={(event) => event.currentTarget.classList.remove("is-over")} 
                    
                    onDrop={(event) => { 
                      event.preventDefault(); 
                      event.currentTarget.classList.remove("is-over"); 
                      stageFile(key, event.dataTransfer.files?.[0]); 
                    }}>

                    {processing[key] ? 
                      <span className="sims-foto-empty">
                        <strong>Mengompres foto...</strong>
                      </span> : preview ? 
                        <img src={preview} alt={`Foto ${name}`} /> : <span className="sims-foto-empty">
                          <span aria-hidden="true">＋</span>
                          <strong>Tarik foto ke sini</strong>
                          <small>atau klik pilih foto</small>
                        </span>}
                  </button>

                  {preview && (staged || activePhoto === key) && 
                    <button 
                      type="button" 
                      className="sims-foto-remove mr-2" 
                      disabled={saving || Boolean(processing[key])} 
                      onClick={() => setDeleteTarget({ key, name, persisted, context: contextKey })} 
                      aria-label={`Hapus foto ${name}`}
                    >
                      ✕ 
                    </button>
                  }

                  {preview && activePhoto === key && 
                    <button 
                    type="button" 
                    className="sims-foto-change" 
                    disabled={saving} 
                    onClick={() => inputRefs.current[key]?.click()}
                    >
                      Ganti foto
                    </button>
                  }
                </div>

                <div className="sims-foto-info">
                  <strong>{toTitleCase(name) }</strong>
                  <div>
                    <span>NISN</span>
                    <b>{student?.nisn || "-"}</b>
                  </div>
                  <div>
                    <span>Kode absen</span>
                    <b>{student?.order_absen ?? "-"}</b>
                  </div>
                </div>

                <div 
                  className={`sims-foto-state ${staged || saved[key] ? "ready" : ""}`}>
                    {processing[key] ? "Mengompres..." : staged ? `✓ Siap disimpan · ${formatSize(staged.file.size)}` : saved[key] ? `✓ Tersimpan · ${formatSize(saved[key].file.size)}` : existing ? "Foto saat ini" : "Belum ada foto"}
                </div>
              </article>;
            })}
          </div> : 
          <div className="sims-foto-empty-list">
            Data siswa tidak ditemukan.
          </div>
        }

        {rows.length > 12 && 
          <div className="sims-foto-pages">
            <button 
              type="button" 
              disabled={page <= 1} 
              onClick={() => setPage(page - 1)}
            >
              Sebelumnya
            </button>
            
            <span>{page} / {pageCount}</span>
            
            <button 
              type="button" 
              disabled={page >= pageCount} 
              onClick={() => setPage(page + 1)}
            >
              Selanjutnya
            </button>
          </div>
        }

        <div className="sims-foto-footer">
          <span role="status">
            {notice || "Foto yang dipilih tampil sebagai pratinjau sebelum disimpan."}
          </span>
          <button 
            type="button" 
            disabled={saving || !count || Object.values(processing).some(Boolean)} 
            onClick={saveAll}
          >
            {saving ? "Menyimpan foto..." : `Simpan semua foto (${count})`}
          </button>
        </div>

        <Modal 
          show={Boolean(deleteTarget)} 
          onHide={() => setDeleteTarget(null)} 
          centered 
          aria-labelledby="sims-foto-delete-title"
        >
          <Modal.Header closeButton>
            <Modal.Title id="sims-foto-delete-title" style={{ fontSize: 18 }}>
              Hapus foto?
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="mt-4">
            <p>Kamu yakin mau hapus foto <strong>{deleteTarget?.name}</strong> ini?</p>
          </Modal.Body>

          <Modal.Footer>
            <div className="sims-foto-confirm-actions">
              <button 
                type="button" 
                onClick={() => setDeleteTarget(null)}
              >
                Batal
              </button>

              <button 
                type="button" 
                className="sims-foto-confirm-delete" 
                disabled={saving} 
                onClick={confirmRemovePhoto}
              >
                Hapus
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>;
    </>
  )
}
